import React, { useState, useRef, useEffect } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { PlusIcon, MinusIcon, ArrowPathIcon } from "@heroicons/react/24/solid";
import { getCountryInfo, findCountryData, initializeCountryRegions } from "../../moduleServices/trips_sanitize";
import CountryBadge from "../../moduleComponents/CountryBadge";
import Spinner from "../../moduleComponents/SpinnerLoad";

const geoUrl = "https://unpkg.com/world-atlas@2.0.2/countries-110m.json";

export default function CardWorldMap({ trips, isTripsLoading }) {
  const mapContainerRef = useRef(null);
  const [visitedCountries, setVisitedCountries] = useState([]);
  const [isCountryRegionsReady, setIsCountryRegionsReady] = useState(false);
  const [position, setPosition] = useState({ coordinates: [0, 0], zoom: 1.1 });
  const [hoverInfo, setHoverInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // ##### Map interaction limits
  const maxZoom = 6;
  const minZoom = 1;

  // ##### Initialize country regions first
  useEffect(() => {
    const initRegions = async () => {
      try {
        await initializeCountryRegions();
        setIsCountryRegionsReady(true);
      } catch (error) {
        console.error('Failed to initialize country regions:', error);
        setIsCountryRegionsReady(true); // Continue anyway
      }
    };
    
    initRegions();
  }, []);

  // ##### Process trips data to identify visited countries
  useEffect(() => {
    // Only process trips after country regions are ready
    if (!isCountryRegionsReady) return;
    
    if (!trips || trips.length === 0) {
      setVisitedCountries([]);
      return;
    }

    const processVisitedCountries = () => {
      const visitedSet = new Set();
      
      trips.forEach(trip => {
        if (trip.places && Array.isArray(trip.places)) {
          trip.places.forEach(place => {
            if (place.country) {
              // Get country info to find the standardized name
              const countryInfo = getCountryInfo(place.country);
              if (countryInfo && countryInfo.name) {
                visitedSet.add(countryInfo.name);
              }
            }
          });
        }
      });
      
      setVisitedCountries(Array.from(visitedSet));
    };

    processVisitedCountries();
  }, [trips, isCountryRegionsReady]);

  const handlePointerEnter = (geo, e) => {
    const rect = mapContainerRef.current?.getBoundingClientRect();
    const relativeX = e.clientX - rect.left + 10;
    const relativeY = e.clientY - rect.top + 10;

    // Get country code using findCountryData with name pattern
    const countryData = findCountryData(geo.properties.name, 'name');
    const countryCode = countryData ? countryData.iso2 : null;

    setHoverInfo({
      name: geo.properties.name,
      code: countryCode,
      x: relativeX,
      y: relativeY,
    });
  };

  const handlePointerLeave = () => {
    setHoverInfo(null);
  };

  // ##### Open modal with country info instead of toggling
  const handleCountryClick = (geo) => {
    // Get country code using findCountryData with name pattern
    const countryData = findCountryData(geo.properties.name, 'name');
    const countryCode = countryData ? countryData.iso2 : null;

    setSelectedCountry({
      name: geo.properties.name,
      code: countryCode
    });
    setShowModal(true);
  };

  const handleZoomIn = () => 
    setPosition((prev) => ({
      ...prev,
      zoom: Math.min(prev.zoom * 1.2, maxZoom)
    }));

  const handleZoomOut = () => 
    setPosition((prev) => ({
      ...prev,
      zoom: Math.max(prev.zoom / 1.2, minZoom)
    }));

  const handleReset = () => setPosition({ coordinates: [0, 0], zoom: 1.1 });

  // ##### Simple modal component
  const CountryModal = () => {
    if (!showModal || !selectedCountry) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-sm mx-4 relative">
          {/* Close button (X) in top-right corner */}
          <button
            onClick={() => setShowModal(false)}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header with country info */}
          <div className="flex items-center gap-3 mb-4 pr-8">
            {selectedCountry.code && (
              <CountryBadge code={selectedCountry.code} size={24} />
            )}
            <h3 className="text-lg font-semibold text-gray-800">
              {selectedCountry.name}
            </h3>
          </div>

          {/* Action message */}
          <p className="text-gray-600 mb-6 text-center">Select action for this country</p>

          {/* Action buttons */}
          <div className="space-y-3">
            <button
              disabled
              className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-500"
            >
              Add Trip
            </button>
            <button
              disabled
              className="w-full bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-gray-500"
            >
              Add to Wishlist
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show loading state while fetching trips OR while country regions are not ready
  if (isTripsLoading || !isCountryRegionsReady) {
    return (
      <div ref={mapContainerRef} className="relative w-full h-auto aspect-[17/9]">
        {/* Map controls */}
        <div className="absolute bottom-2 left-2 flex flex-col gap-1 bg-gray-100 rounded-md shadow-md">
          <button onClick={handleZoomIn} className="p-1 hover:bg-gray-200 rounded">
            <PlusIcon className="w-5 h-5" />
          </button>
          <button onClick={handleZoomOut} className="p-1 hover:bg-gray-200 rounded">
            <MinusIcon className="w-5 h-5" />
          </button>
          <button onClick={handleReset} className="p-1 hover:bg-gray-200 rounded">
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Spinner overlay */}
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <Spinner />
        </div>

        {/* Gray map (gray-300) */}
        <ComposableMap
          projection="geoNaturalEarth1"
          projectionConfig={{ scale: 140, center: [0, 0] }}
          width={850}
          height={450}
          style={{ width: "100%", height: "auto" }}
        >
          <ZoomableGroup 
            zoom={position.zoom}
            center={position.coordinates}
            onMoveEnd={setPosition}
            maxZoom={maxZoom}
            minZoom={minZoom}
            translateExtent={[
              [0, 0],
              [850, 450]
            ]}
          >
            <Geographies geography={geoUrl}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#d1d5db"
                    stroke="#9ca3af"
                    style={{
                      default: { outline: "none" },
                      hover: { fill: "#d1d5db", cursor: "default", outline: "none" },
                      pressed: { fill: "#d1d5db", outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>
          </ZoomableGroup>
        </ComposableMap>
      </div>
    );
  }

  return (
    <div ref={mapContainerRef} className="relative w-full h-auto aspect-[17/9]">
      <div className="absolute bottom-2 left-2 flex flex-col gap-1 bg-gray-100 rounded-md shadow-md">
        <button onClick={handleZoomIn} className="p-1 hover:bg-gray-200 rounded">
          <PlusIcon className="w-5 h-5" />
        </button>
        <button onClick={handleZoomOut} className="p-1 hover:bg-gray-200 rounded">
          <MinusIcon className="w-5 h-5" />
        </button>
        <button onClick={handleReset} className="p-1 hover:bg-gray-200 rounded">
          <ArrowPathIcon className="w-5 h-5" />
        </button>
      </div>

      {hoverInfo && (
        <div
          className="absolute bg-white border border-gray-300 rounded shadow px-2 py-1 text-xs flex items-center space-x-2"
          style={{ top: hoverInfo.y, left: hoverInfo.x }}
        >
          {hoverInfo.code && (
            <CountryBadge code={hoverInfo.code} size={16} />
          )}
          <span>{hoverInfo.name}</span>
        </div>
      )}

      <ComposableMap
        projection="geoNaturalEarth1"
        projectionConfig={{ scale: 140, center: [0, 0] }}
        width={850}
        height={450}
        style={{ width: "100%", height: "auto" }}
      >
        <ZoomableGroup 
          zoom={position.zoom}
          center={position.coordinates}
          onMoveEnd={setPosition}
          maxZoom={maxZoom}
          minZoom={minZoom}
          translateExtent={[
            [0, 0],
            [850, 450]
          ]}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                // Use geo.properties.name to match with visitedCountries
                const isVisited = visitedCountries.includes(geo.properties.name);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onPointerUp={() => handleCountryClick(geo)}
                    onPointerEnter={(e) => handlePointerEnter(geo, e)}
                    onPointerLeave={handlePointerLeave}
                    fill={isVisited ? "#4ade80" : "#687286"}
                    stroke="#fff"
                    style={{
                      default: { outline: "none" },
                      hover: { fill: "#60a5fa", cursor: "pointer", outline: "none" },
                      pressed: { fill: "#1e1e1e", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ZoomableGroup>
      </ComposableMap>

      {/* Render modal */}
      <CountryModal />
    </div>
  );
}